/**
 * useRadioSession
 *
 * React view of the MeshCore radio session plus the user's network profile:
 * what the radio REPORTS, whether that is legal for the chosen region,
 * whether it matches the chosen profile, and an apply() that retunes and
 * verifies by read-back.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { meshcoreSession, type RadioState } from '@/lib/meshcore'
import type { ScanProgress, ScanResult } from '@/lib/meshcore/session'
import {
  BUILT_IN_PROFILES,
  defaultProfile,
  findProfile,
  radioMatches,
  type LoRaNetworkProfile,
  type MeshCoreProfile,
} from '@/lib/lora/profiles'
import {
  getCustomProfiles,
  getSelectedProfileId,
  saveCustomProfile,
  setSelectedProfileId,
} from '@/lib/storage/lora-profile'

export interface UseRadioSessionReturn {
  radio: RadioState
  profiles: LoRaNetworkProfile[]
  selected: LoRaNetworkProfile
  /** null when no radio is connected or the profile is not MeshCore. */
  radioMatchesSelected: boolean | null
  applying: boolean
  applyError: string | null
  selectProfile: (id: string) => void
  addCustomProfile: (p: LoRaNetworkProfile) => void
  /** Retune the connected MeshCore radio to the selected profile. */
  applySelected: () => Promise<void>
  /** "Find a mesh": try every MeshCore profile for the chosen region. */
  scan: () => Promise<void>
  scanProgress: ScanProgress | null
  scanResults: ScanResult[] | null
  /** Seconds the radio listens on each network during a scan. */
  scanListenSeconds: number
}

export const SCAN_LISTEN_SECONDS = 30

export function useRadioSession(): UseRadioSessionReturn {
  const [radio, setRadio] = useState<RadioState>(meshcoreSession.snapshot)
  const [custom, setCustom] = useState<LoRaNetworkProfile[]>(() => getCustomProfiles())
  const [selectedId, setSelectedId] = useState<string>(() => getSelectedProfileId())
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null)
  const [scanResults, setScanResults] = useState<ScanResult[] | null>(null)

  useEffect(() => meshcoreSession.onState(setRadio), [])

  const profiles = useMemo(() => [...BUILT_IN_PROFILES, ...custom], [custom])
  const selected = useMemo(() => findProfile(selectedId, custom) ?? defaultProfile(), [selectedId, custom])

  // The chosen profile's region is the legal band the session guards.
  useEffect(() => {
    meshcoreSession.setRegion(selected.region)
  }, [selected.region])

  const reported = radio.selfInfo?.radio
  const radioMatchesSelected =
    reported && selected.firmware === 'meshcore' ? radioMatches(selected, reported) : null

  const selectProfile = useCallback((id: string) => {
    setSelectedId(id)
    setSelectedProfileId(id)
    setApplyError(null)
  }, [])

  const addCustomProfile = useCallback((p: LoRaNetworkProfile) => {
    setCustom(saveCustomProfile(p))
    setSelectedId(p.id)
    setSelectedProfileId(p.id)
  }, [])

  const applySelected = useCallback(async () => {
    if (selected.firmware !== 'meshcore') {
      setApplyError('This profile is for Meshtastic radios. Your radio runs MeshCore.')
      return
    }
    setApplying(true)
    setApplyError(null)
    try {
      await meshcoreSession.applyRadio({
        freqMHz: selected.freqMHz,
        bwKHz: selected.bwKHz,
        sf: selected.sf,
        cr: selected.cr,
      })
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : String(e))
    } finally {
      setApplying(false)
    }
  }, [selected])

  const scan = useCallback(async () => {
    setApplyError(null)
    setScanResults(null)
    try {
      const candidates = profiles.filter((p): p is MeshCoreProfile => p.firmware === 'meshcore')
      const results = await meshcoreSession.scan(candidates, {
        listenMs: SCAN_LISTEN_SECONDS * 1000,
        onProgress: setScanProgress,
      })
      setScanResults(results)
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : String(e))
    } finally {
      setScanProgress(null)
    }
  }, [profiles])

  return {
    scan,
    scanProgress,
    scanResults,
    scanListenSeconds: SCAN_LISTEN_SECONDS,
    radio,
    profiles,
    selected,
    radioMatchesSelected,
    applying,
    applyError,
    selectProfile,
    addCustomProfile,
    applySelected,
  }
}
